// src/rms/data/taxService.js
// ============================================================================
// Reading and writing an operator's tax configuration
// ============================================================================
// taxEngine.js does the arithmetic and knows nothing about storage. This file
// is the other half: it loads an operator's rates, groups and category rules
// into the shape the engine expects, and provisions a starting set when a
// business is created.
//
// The load is one call per screen, not one per invoice line. runRecurringBilling
// raises an invoice per subscription in a loop, so the config is hoisted out and
// passed in rather than re-fetched per iteration.
// ============================================================================

import db from "./index";
import { taxLabelForCountry } from "../i18n/countries";
import { createSuccessResponse, createErrorResponse } from "../utils/response";

/**
 * Every kind of line an invoice can carry, in the order the settings screen
 * shows them. Kept here rather than in the migration's CHECK because the screen
 * needs labels and an order; the CHECK is the guard, this is the presentation.
 */
export const TAXABLE_LINE_KINDS = [
  { kind: "rental", label: "Rental" },
  { kind: "addon", label: "Add-ons" },
  { kind: "delivery", label: "Delivery" },
  { kind: "usage_overage", label: "Usage overage" },
  { kind: "damage", label: "Damage" },
  { kind: "cleaning", label: "Cleaning" },
  { kind: "late_fee", label: "Late fees" },
  { kind: "deposit_forfeit", label: "Forfeited deposit" },
  { kind: "other", label: "Other" },
];

/**
 * Loads everything the engine needs in one go.
 *
 * Returns a config even when nothing is configured: `groups` empty and every
 * category unmapped means "charge no tax", which is precisely the behaviour
 * every operator had before tax existed.
 */
export async function loadTaxConfig(operator) {
  if (!operator?.id) return emptyConfig(operator);

  const [ratesRes, groupsRes, linksRes, defaultsRes] = await Promise.all([
    db.list("taxRates", { operator_id: operator.id }, { orderBy: "sequence" }),
    db.list("taxGroups", { operator_id: operator.id }, { orderBy: "name" }),
    db.list("taxGroupRates", { operator_id: operator.id }),
    db.list("taxCategoryDefaults", { operator_id: operator.id }),
  ]);

  const rates = ratesRes.data || [];
  const groupRows = groupsRes.data || [];
  const links = linksRes.data || [];
  const defaults = defaultsRes.data || [];

  const ratesById = new Map(rates.map((r) => [r.id, r]));

  const groups = new Map();
  for (const g of groupRows) {
    if (g.is_active === false) continue;
    groups.set(g.id, {
      id: g.id,
      name: g.name,
      is_default: !!g.is_default,
      rates: links
        .filter((l) => l.tax_group_id === g.id)
        .map((l) => ratesById.get(l.tax_rate_id))
        .filter((r) => r && r.is_active !== false),
    });
  }

  const byKind = {};
  for (const row of defaults) byKind[row.line_kind] = row.tax_group_id || null;

  const defaultGroup = groupRows.find((g) => g.is_default && g.is_active !== false) || null;

  return {
    mode: operator.prices_include_tax ? "inclusive" : "exclusive",
    currency: operator.currency || "USD",
    // Override first, then the term the operator's country uses, then a
    // generic. Deriving from the country rather than copying it onto the
    // operator at signup means moving the business to another country moves
    // the wording with it, instead of leaving last country's term on every
    // invoice until somebody notices.
    registration: operator.tax_registration_number
      ? {
          label:
            operator.tax_registration_label ||
            taxLabelForCountry(operator.country) ||
            "Tax No.",
          number: operator.tax_registration_number,
        }
      : null,
    groups,
    byKind,
    defaultGroupId: defaultGroup ? defaultGroup.id : null,
    rates,
    groupRows,
    links,
    /** Which group taxes a given kind of line, or null for untaxed. */
    groupFor(kind) {
      // An explicit row wins, including an explicit NULL meaning "not taxed".
      if (Object.prototype.hasOwnProperty.call(byKind, kind)) return byKind[kind];
      return this.defaultGroupId;
    },
  };
}

function emptyConfig(operator) {
  return {
    mode: operator?.prices_include_tax ? "inclusive" : "exclusive",
    currency: operator?.currency || "USD",
    registration: null,
    groups: new Map(),
    byKind: {},
    defaultGroupId: null,
    rates: [],
    groupRows: [],
    links: [],
    groupFor: () => null,
  };
}

/**
 * Creates the starting tax setup for a brand new business from the
 * question asked during onboarding.
 *
 * Passing no rate is allowed and creates nothing: an operator who does not know
 * their rate yet gets invoices that say so, which is better than a number we
 * invented. See the note on the onboarding screen.
 */
export async function provisionTaxDefaults(operator, taxes = []) {
  if (!operator?.id) return createErrorResponse("No operator to configure tax for.");

  // Tolerate the single-object shape this used to take, so an older caller
  // cannot silently provision nothing.
  const list = (Array.isArray(taxes) ? taxes : [taxes])
    .filter((t) => t && t.name && t.ratePpm !== null && t.ratePpm !== undefined);

  if (!list.length) return createSuccessResponse({ skipped: "no rate given" });

  // Sequence in tens, so a rate can later be slipped between two existing ones
  // without renumbering the rest. Order only decides anything once something
  // compounds, and nothing set up here does -- compounding is rare enough that
  // asking about it during onboarding would cost more than it earns.
  const rates = [];
  for (const [i, tax] of list.entries()) {
    rates.push(
      (
        await db.create("taxRates", {
          operator_id: operator.id,
          name: tax.name,
          rate_ppm: tax.ratePpm,
          compound: false,
          sequence: (i + 1) * 10,
          is_active: true,
        })
      ).data
    );
  }

  const group = (
    await db.create("taxGroups", {
      operator_id: operator.id,
      name: "Standard",
      description: "Applied to everything unless you say otherwise.",
      is_default: true,
      is_active: true,
    })
  ).data;

  // Every rate joins the one default group, which is what "GST 5% + PST 7%"
  // means: both are charged together on the same base.
  for (const rate of rates) {
    await db.create("taxGroupRates", {
      operator_id: operator.id,
      tax_group_id: group.id,
      tax_rate_id: rate.id,
    });
  }

  // Every kind of charge is taxed at the standard rate to begin with, EXCEPT a
  // forfeited deposit. That one is taxable in most places, but charging tax on
  // it because we assumed would be putting words in the operator's mouth about
  // their own liability.
  for (const { kind } of TAXABLE_LINE_KINDS) {
    await db.create("taxCategoryDefaults", {
      operator_id: operator.id,
      line_kind: kind,
      tax_group_id: kind === "deposit_forfeit" ? null : group.id,
    });
  }

  return createSuccessResponse({ rates, group });
}

// ----------------------------------------------------------------------------
// Editing
// ----------------------------------------------------------------------------

export async function saveRate(operator, draft) {
  const payload = {
    operator_id: operator.id,
    name: draft.name,
    authority: draft.authority || null,
    rate_ppm: draft.rate_ppm,
    compound: !!draft.compound,
    sequence: draft.sequence ?? 100,
    registration_number: draft.registration_number || null,
    show_registration_on_invoice: !!draft.show_registration_on_invoice,
    effective_from: draft.effective_from || null,
    effective_to: draft.effective_to || null,
    is_active: draft.is_active !== false,
  };
  return draft.id ? db.update("taxRates", draft.id, payload) : db.create("taxRates", payload);
}

/**
 * Rates in use are deactivated rather than deleted.
 *
 * The database enforces this too -- rms_tax_group_rates references the rate ON
 * DELETE RESTRICT -- but failing here gives the screen something to say instead
 * of surfacing a foreign-key violation.
 */
export async function removeRate(operator, rateId, links) {
  const usedBy = (links || []).filter((l) => l.tax_rate_id === rateId);
  if (usedBy.length) {
    await db.update("taxRates", rateId, { is_active: false });
    return createSuccessResponse({ deactivated: true, groups: usedBy.length });
  }
  await db.remove("taxRates", rateId);
  return createSuccessResponse({ deleted: true });
}

export async function saveGroup(operator, draft, rateIds) {
  const payload = {
    operator_id: operator.id,
    name: draft.name,
    description: draft.description || null,
    is_default: !!draft.is_default,
    is_active: draft.is_active !== false,
  };

  // One default per operator is enforced by a partial unique index, so the
  // previous holder has to stand down first or the insert is rejected.
  if (payload.is_default) {
    const existing = (await db.list("taxGroups", { operator_id: operator.id })).data || [];
    for (const g of existing) {
      if (g.is_default && g.id !== draft.id) {
        await db.update("taxGroups", g.id, { is_default: false });
      }
    }
  }

  const group = draft.id
    ? (await db.update("taxGroups", draft.id, payload)).data
    : (await db.create("taxGroups", payload)).data;

  if (Array.isArray(rateIds)) {
    const links = (await db.list("taxGroupRates", { operator_id: operator.id, tax_group_id: group.id })).data || [];
    const have = new Set(links.map((l) => l.tax_rate_id));
    const want = new Set(rateIds);

    for (const link of links) {
      if (!want.has(link.tax_rate_id)) await db.remove("taxGroupRates", link.id);
    }
    for (const rateId of want) {
      if (!have.has(rateId)) {
        await db.create("taxGroupRates", {
          operator_id: operator.id,
          tax_group_id: group.id,
          tax_rate_id: rateId,
        });
      }
    }
  }

  return createSuccessResponse(group);
}

export async function setCategoryGroup(operator, lineKind, groupId, existing) {
  const row = (existing || []).find((d) => d.line_kind === lineKind);
  if (row) return db.update("taxCategoryDefaults", row.id, { tax_group_id: groupId || null });
  return db.create("taxCategoryDefaults", {
    operator_id: operator.id,
    line_kind: lineKind,
    tax_group_id: groupId || null,
  });
}

export default {
  loadTaxConfig,
  provisionTaxDefaults,
  saveRate,
  removeRate,
  saveGroup,
  setCategoryGroup,
  TAXABLE_LINE_KINDS,
};
